import type { Language } from '@/shared/i18n/types'

/**
 * One place for every date the app renders, replacing the inline
 * `toLocaleDateString('pt-BR', …)` calls that were scattered across the tree.
 *
 * **Timezone is deliberately not localised.** Kickoffs stay in
 * `America/São_Paulo` in both languages: a 16:00 kickoff is a São Paulo kickoff
 * whether you read the page in Portuguese or English, and re-basing it to the
 * viewer's own zone would silently move every fixture time in the app. Only the
 * *language* of the words changes.
 */

export const MATCH_TIME_ZONE = 'America/Sao_Paulo'

/**
 * `en` is formatted as en-GB, not en-US.
 *
 * Bare 'en' resolves to US conventions, which puts the month first ("Jun 16",
 * "06/16/2026"). That reads day-first everywhere else in the app and in the
 * pt-BR sibling, so a reader switching languages would see the same fixture
 * reorder itself. en-GB keeps day-first in both.
 */
function localeFor(language: Language): string {
  return language === 'en' ? 'en-GB' : language
}

function opts(extra: Intl.DateTimeFormatOptions): Intl.DateTimeFormatOptions {
  return { timeZone: MATCH_TIME_ZONE, ...extra }
}

/**
 * "16:00" — 24-hour in BOTH languages.
 *
 * English would default to "04:00 PM", which changes the digits a player sees
 * for the same kickoff and makes a fixture column ragged. Football schedules are
 * quoted on a 24-hour clock anyway, so `h23` is pinned rather than localised.
 */
export function formatKickoffTime(iso: string, language: Language): string {
  return new Date(iso).toLocaleTimeString(localeFor(language), opts({
    hour:      '2-digit',
    minute:    '2-digit',
    hourCycle: 'h23',
  }))
}

/** "16/06" · "16 Jun" */
export function formatShortDate(iso: string, language: Language): string {
  return new Date(iso).toLocaleDateString(localeFor(language), opts({
    day:   '2-digit',
    month: language === 'pt-BR' ? '2-digit' : 'short',
  }))
}

/** "terça-feira" · "Tuesday" */
export function formatWeekday(date: Date, language: Language): string {
  return date.toLocaleDateString(localeFor(language), opts({ weekday: 'long' }))
}

/** "16 de junho" · "16 June" */
export function formatDayMonth(date: Date, language: Language): string {
  return date.toLocaleDateString(localeFor(language), opts({ day: 'numeric', month: 'long' }))
}

/**
 * "16/06/2026" · "16 Jun 2026".
 *
 * English does NOT get the all-numeric form: `en` resolves to US ordering, so
 * 16/06 would render "06/16/2026" and a reader has no way to tell which half is
 * the month. A named month removes the ambiguity instead of hiding it.
 */
export function formatDate(iso: string, language: Language): string {
  return new Date(iso).toLocaleDateString(
    localeFor(language),
    opts(
      language === 'pt-BR'
        ? { day: '2-digit', month: '2-digit', year: 'numeric' }
        : { day: '2-digit', month: 'short', year: 'numeric' },
    ),
  )
}

/** True when both instants fall on the same São Paulo calendar day. */
export function isSameMatchDay(a: Date, b: Date): boolean {
  const key = (d: Date) => d.toLocaleDateString('en-CA', { timeZone: MATCH_TIME_ZONE })
  return key(a) === key(b)
}
